import { useTranslation } from "react-i18next";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, Box, Stack, Typography } from "@mui/material";

import { formatEuro } from "../../../../../../format/formatNumber";
import { SeizableIncomeAssessmentResponse } from "../../../../../api/ClientFileApi";
import { FieldInfo } from "../../../personal-data/components/field-info/FieldInfo";
import { IncomeSectionCard } from "../income-section-card/IncomeSectionCard";
import { explanationText, resultText } from "./SeizableAmountSection.utils";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.seizableAmount";

interface Props {
  assessment: SeizableIncomeAssessmentResponse | undefined;
}

interface FigureRowProps {
  label: string;
  value: string;
  source?: string;
}

function FigureRow({ label, value, source }: FigureRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {source && <LockOutlinedIcon fontSize="small" color="action" aria-hidden />}
        <Stack direction="row" alignItems="baseline" columnGap={1} flexWrap="wrap">
          <Typography variant="body2">{label}</Typography>
          {source && (
            <Typography variant="caption" color="text.secondary">
              {source}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function SeizableAmountSection({ assessment }: Props) {
  const { t, i18n } = useTranslation();
  const euro = (value: number) => formatEuro(value, i18n.language);

  if (!assessment) {
    return null;
  }

  const isSeizable = assessment.seizableAmount12 > 0 || assessment.seizableAmount14 > 0;
  const summary = resultText(assessment, t, i18n.language);

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <Stack gap={1}>
        <FigureRow label={t(`${SECTION}.income12`)} value={euro(assessment.income12)} />
        <FigureRow label={t(`${SECTION}.income14`)} value={euro(assessment.income14)} />
        {assessment.nonSeizableIncome > 0 && (
          <FigureRow label={t(`${SECTION}.nonSeizableIncome`)} value={euro(assessment.nonSeizableIncome)} />
        )}
        {assessment.subsistenceAmount12 != null && (
          <FigureRow
            label={t(`${SECTION}.subsistenceAmount12`)}
            value={euro(assessment.subsistenceAmount12)}
            source={t(`${SECTION}.fromSettings`)}
          />
        )}
        {assessment.subsistenceAmount14 != null && (
          <FigureRow
            label={t(`${SECTION}.subsistenceAmount14`)}
            value={euro(assessment.subsistenceAmount14)}
            source={t(`${SECTION}.fromSettings`)}
          />
        )}
        <FigureRow
          label={t(`${SECTION}.maintenanceObligationCount`)}
          value={String(assessment.maintenanceObligationCount)}
          source={t(`${SECTION}.fromRelatives`)}
        />
      </Stack>
      {assessment.isCalculated ? (
        <Stack direction="row" alignItems="flex-start" gap={0.5} aria-live="polite">
          <Box flex={1}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {summary}
            </Typography>
            {isSeizable && (
              <Typography variant="body1">
                {t(`${SECTION}.remainingIncome`, { amount: euro(assessment.remainingIncome) })}
              </Typography>
            )}
          </Box>
          <FieldInfo fieldLabel={t(`${SECTION}.title`)} dense>
            <Stack gap={1}>
              <Typography variant="body2">{t(`${SECTION}.explanation.intro`)}</Typography>
              {assessment.explanation.map((line, index) => (
                <Typography key={`${line.kind}-${index}`} variant="body2">
                  {explanationText(line, t, i18n.language)}
                </Typography>
              ))}
              <Typography variant="body2">
                {t(`${SECTION}.explanation.maintenanceObligations`, { count: assessment.maintenanceObligationCount })}
              </Typography>
            </Stack>
          </FieldInfo>
        </Stack>
      ) : (
        <Alert severity="warning">{t(`${SECTION}.parametersMissing`)}</Alert>
      )}
    </IncomeSectionCard>
  );
}
